import api from './api';
import type {
  ProductionOrder,
  ProductionOrderLine,
  ProductionOrderAvailability,
  ProductionPostingResult,
  PaginatedResponse,
} from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/production-orders`;

// ── Payloads ──────────────────────────────────────────────────────────────────

export interface CreateProductionOrderPayload {
  recipeId: string;
  plannedQuantity: number;
  locationId?: string;
  outputOwnershipAccountId?: string;
  plannedDate?: string | null;
  outputExpiryDate?: string | null;
  outputLotNumber?: string | null;
  notes?: string | null;
}

export interface UpdateProductionOrderPayload {
  plannedQuantity?: number;
  locationId?: string;
  outputOwnershipAccountId?: string;
  plannedDate?: string | null;
  outputExpiryDate?: string | null;
  outputLotNumber?: string | null;
  notes?: string | null;
}

export interface UpdateProductionOrderLinePayload {
  fromLocationId?: string;
  sourceOwnershipAccountId?: string;
  lotId?: string | null;
}

export interface ListProductionOrdersParams {
  page?: number;
  pageSize?: number;
  status?: 'DRAFT' | 'POSTED';
}

// ── Service ───────────────────────────────────────────────────────────────────

const productionOrderService = {
  listProductionOrders: async (
    venueId: string,
    params: ListProductionOrdersParams = {},
  ): Promise<PaginatedResponse<ProductionOrder>> => {
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  createProductionOrder: async (
    venueId: string,
    payload: CreateProductionOrderPayload,
  ): Promise<{ order: ProductionOrder; lines: ProductionOrderLine[] }> => {
    const response = await api.post(base(venueId), payload);
    return response.data;
  },

  getProductionOrder: async (
    venueId: string,
    orderId: string,
  ): Promise<{ order: ProductionOrder; lines: ProductionOrderLine[] }> => {
    const response = await api.get(`${base(venueId)}/${orderId}`);
    return response.data;
  },

  updateProductionOrder: async (
    venueId: string,
    orderId: string,
    payload: UpdateProductionOrderPayload,
  ): Promise<{ order: ProductionOrder; lines: ProductionOrderLine[] }> => {
    const response = await api.patch(`${base(venueId)}/${orderId}`, payload);
    return response.data;
  },

  deleteProductionOrder: async (
    venueId: string,
    orderId: string,
  ): Promise<void> => {
    await api.delete(`${base(venueId)}/${orderId}`);
  },

  listProductionOrderLines: async (
    venueId: string,
    orderId: string,
  ): Promise<{ order: ProductionOrder; lines: ProductionOrderLine[] }> => {
    const response = await api.get(`${base(venueId)}/${orderId}/lines`);
    return response.data;
  },

  updateProductionOrderLine: async (
    venueId: string,
    orderId: string,
    lineId: string,
    payload: UpdateProductionOrderLinePayload,
  ): Promise<ProductionOrderLine> => {
    const response = await api.patch(`${base(venueId)}/${orderId}/lines/${lineId}`, payload);
    return response.data;
  },

  getProductionAvailability: async (
    venueId: string,
    orderId: string,
  ): Promise<ProductionOrderAvailability> => {
    const response = await api.get(`${base(venueId)}/${orderId}/availability`);
    return response.data;
  },

  postProductionOrder: async (
    venueId: string,
    orderId: string,
  ): Promise<ProductionPostingResult> => {
    const response = await api.post(`${base(venueId)}/${orderId}/post`);
    return response.data;
  },

  syncProductionOrderRecipe: async (
    venueId: string,
    orderId: string,
  ): Promise<{ order: ProductionOrder; lines: ProductionOrderLine[] }> => {
    const response = await api.post(`${base(venueId)}/${orderId}/sync-recipe`);
    return response.data;
  },
};

export default productionOrderService;
